// src/Views/PortfolioGallery.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import { SPACING_TIGHT, SPACING_COMPACT, SPACING_BUTTON, SPACING_CARD, SPACING_CARD_BODY, SPACING_GROUP, SPACING_SECTION, SPACING_SECTION_LARGE, SPACING_PAGE } from '../constants/layout';

// Static fallback images for when Firebase has no data
const STATIC_IMAGES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19];

function PagerPreloader({ items, getBefore, getAfter }) {
  useEffect(() => {
    if (typeof window === 'undefined' || !items?.length) return;
    items.forEach((item) => {
      const a = new window.Image();
      a.src = getBefore(item);
      const b = new window.Image();
      b.src = getAfter(item);
    });
  }, [items, getBefore, getAfter]);
  return null;
}

export default function PortfolioGallery({ limit, viewMorePath, limitRows, pager }) {
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [pagerIndex, setPagerIndex] = useState(0);

  useEffect(() => {
    const colRef = collection(db, 'beforeAfterPortfolio');

    const unsubscribe = onSnapshot(colRef, (snapshot) => {
      const items = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(item => item.is_active !== false)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      setPortfolioItems(items);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching portfolio:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Use Firebase items if available, otherwise fall back to static images
  const hasFirebaseData = portfolioItems.length > 0;
  const displayItems = hasFirebaseData
    ? (limit ? portfolioItems.slice(0, limit) : portfolioItems)
    : (limit ? STATIC_IMAGES.slice(0, limit) : STATIC_IMAGES);

  // For navigation in lightbox
  const allItems = hasFirebaseData ? portfolioItems : STATIC_IMAGES;

  // Check if there are more items than displayed
  const hasMore = limit && allItems.length > limit;

  const getBeforeImageUrl = (item) => {
    if (hasFirebaseData) {
      return item.before_image_url;
    }
    return `/images/portfolio-${item.toString().padStart(2, '0')}-left.jpg`;
  };

  const getAfterImageUrl = (item) => {
    if (hasFirebaseData) {
      return item.after_image_url;
    }
    return `/images/portfolio-${item.toString().padStart(2, '0')}-right.jpg`;
  };

  const getBeforeThumbnailUrl = (item) => {
    if (hasFirebaseData) {
      return item.before_thumbnail_url || item.before_image_url;
    }
    return `/images/portfolio-${item.toString().padStart(2, '0')}-left.jpg`;
  };

  const getAfterThumbnailUrl = (item) => {
    if (hasFirebaseData) {
      return item.after_thumbnail_url || item.after_image_url;
    }
    return `/images/portfolio-${item.toString().padStart(2, '0')}-right.jpg`;
  };

  const getItemKey = (item) => {
    return hasFirebaseData ? item.id : item;
  };

  const getItemTitle = (item) => {
    return hasFirebaseData ? (item.title || 'Portfolio Item') : `Portfolio ${item}`;
  };

  if (loading && !pager) {
    return (
      <div className="gallery-loading">
        <div className="loading-spinner" />
        <style jsx>{`
          .gallery-loading {
            display: flex;
            justify-content: center;
            padding: ${SPACING_PAGE} 0;
          }
          .loading-spinner {
            width: 40px;
            height: 40px;
            border: 3px solid #f3f3f3;
            border-top: 3px solid #333;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (pager) {
    const isReady = !loading && allItems.length > 0;
    const pagerItem = isReady ? allItems[pagerIndex] : null;
    const goPrev = () => {
      if (!isReady) return;
      setPagerIndex(pagerIndex > 0 ? pagerIndex - 1 : allItems.length - 1);
    };
    const goNext = () => {
      if (!isReady) return;
      setPagerIndex(pagerIndex < allItems.length - 1 ? pagerIndex + 1 : 0);
    };

    return (
      <div className="pager-row">
        {isReady && (
          <PagerPreloader items={allItems} getBefore={getBeforeThumbnailUrl} getAfter={getAfterThumbnailUrl} />
        )}

        <button type="button" className="pager-arrow" onClick={goPrev} aria-label="Previous" disabled={!isReady}>
          <svg className="pager-chevron" viewBox="0 0 24 80" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="18,8 6,40 18,72" />
          </svg>
        </button>

        {viewMorePath ? (
          <Link to={viewMorePath} className="pager-pair pager-pair-link" aria-label="See more of our work">
            <div className="pager-img-placeholder">
              <span className="pager-label">Before</span>
              {isReady && (
                <img
                  key={`before-${getItemKey(pagerItem)}`}
                  src={getBeforeThumbnailUrl(pagerItem)}
                  alt={`Before - ${getItemTitle(pagerItem)}`}
                  className="pager-half-img"
                  onLoad={(e) => e.target.setAttribute('data-loaded', 'true')}
                />
              )}
            </div>
            <div className="pager-img-placeholder">
              <span className="pager-label">After</span>
              {isReady && (
                <img
                  key={`after-${getItemKey(pagerItem)}`}
                  src={getAfterThumbnailUrl(pagerItem)}
                  alt={`After - ${getItemTitle(pagerItem)}`}
                  className="pager-half-img"
                  onLoad={(e) => e.target.setAttribute('data-loaded', 'true')}
                />
              )}
            </div>
          </Link>
        ) : (
          <div className="pager-pair">
            <div className="pager-img-placeholder">
              <span className="pager-label">Before</span>
              {isReady && (
                <img
                  key={`before-${getItemKey(pagerItem)}`}
                  src={getBeforeThumbnailUrl(pagerItem)}
                  alt={`Before - ${getItemTitle(pagerItem)}`}
                  className="pager-half-img"
                  onLoad={(e) => e.target.setAttribute('data-loaded', 'true')}
                />
              )}
            </div>
            <div className="pager-img-placeholder">
              <span className="pager-label">After</span>
              {isReady && (
                <img
                  key={`after-${getItemKey(pagerItem)}`}
                  src={getAfterThumbnailUrl(pagerItem)}
                  alt={`After - ${getItemTitle(pagerItem)}`}
                  className="pager-half-img"
                  onLoad={(e) => e.target.setAttribute('data-loaded', 'true')}
                />
              )}
            </div>
          </div>
        )}

        <button type="button" className="pager-arrow" onClick={goNext} aria-label="Next" disabled={!isReady}>
          <svg className="pager-chevron" viewBox="0 0 24 80" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="6,8 18,40 6,72" />
          </svg>
        </button>

        <style jsx>{`
          .pager-row {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: ${SPACING_CARD};
            width: 100%;
          }

          .pager-arrow {
            flex-shrink: 0;
            background: transparent;
            border: none;
            padding: ${SPACING_TIGHT};
            cursor: pointer;
            color: var(--color-text-muted);
            opacity: 0.7;
            transition: opacity 0.15s ease, color 0.15s ease;
          }

          .pager-arrow:hover:not(:disabled) {
            color: var(--color-accent);
            opacity: 1;
          }

          .pager-arrow:disabled {
            cursor: default;
            opacity: 0.35;
          }

          .pager-chevron {
            width: 18px;
            height: 60px;
            display: block;
          }

          .pager-pair {
            display: flex;
            gap: ${SPACING_TIGHT};
            flex: 1;
            max-width: 720px;
            aspect-ratio: 3 / 2;
          }

          .pager-pair-link {
            text-decoration: none;
            color: inherit;
            cursor: pointer;
            transition: transform 0.2s ease;
          }

          .pager-pair-link:hover {
            transform: scale(1.01);
          }

          .pager-img-placeholder {
            position: relative;
            flex: 1;
            height: 100%;
            background: linear-gradient(
              90deg,
              #e5e5e5 0%,
              #efefef 50%,
              #e5e5e5 100%
            );
            background-size: 200% 100%;
            animation: pager-shimmer 1.5s infinite;
            border-radius: ${SPACING_TIGHT};
            overflow: hidden;
          }

          @keyframes pager-shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }

          .pager-half-img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
            opacity: 0;
            transition: opacity 0.25s ease-in-out;
          }

          .pager-half-img[data-loaded="true"] {
            opacity: 1;
          }

          .pager-label {
            position: absolute;
            bottom: ${SPACING_CARD};
            left: ${SPACING_CARD};
            color: #fff;
            font-size: 24px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.02em;
            text-shadow: 0 2px 12px rgba(0, 0, 0, 0.9), 0 0 20px rgba(0, 0, 0, 0.6);
            z-index: 1;
            pointer-events: none;
          }

          @media (max-width: 768px) {
            .pager-row {
              gap: ${SPACING_TIGHT};
            }

            .pager-chevron {
              width: 14px;
              height: 48px;
            }

            .pager-label {
              font-size: 18px;
              bottom: ${SPACING_COMPACT};
              left: ${SPACING_COMPACT};
            }
          }

          @media (max-width: 480px) {
            .pager-chevron {
              width: 12px;
              height: 40px;
            }

            .pager-label {
              font-size: 14px;
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <div className={`gallery-grid${limitRows ? ' limit-rows' : ''}`}>
        {displayItems.map((item, index) => (
          <div key={getItemKey(item)} className="gallery-item" onClick={() => setSelectedIndex(index)}>
            <div className="gallery-img-placeholder">
              <img
                src={getBeforeThumbnailUrl(item)}
                alt={`Before - ${getItemTitle(item)}`}
                className="gallery-half-img"
                onLoad={(e) => e.target.setAttribute('data-loaded', 'true')}
              />
            </div>
            <div className="gallery-img-placeholder">
              <img
                src={getAfterThumbnailUrl(item)}
                alt={`After - ${getItemTitle(item)}`}
                className="gallery-half-img"
                onLoad={(e) => e.target.setAttribute('data-loaded', 'true')}
              />
            </div>
          </div>
        ))}
      </div>

      {viewMorePath && hasMore && (
        <div className="gallery-view-more">
          <Link to={viewMorePath} className="gallery-view-more-button">
            View More
          </Link>
        </div>
      )}

      {/* Lightbox Modal */}
      {selectedIndex !== null && (
        <div className="lightbox-overlay" onClick={() => setSelectedIndex(null)}>
          <button className="lightbox-close" onClick={() => setSelectedIndex(null)}>
            <span className="material-icons">close</span>
          </button>

          <button
            className="lightbox-nav lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedIndex(selectedIndex > 0 ? selectedIndex - 1 : allItems.length - 1);
            }}
          >
            <span className="material-icons">chevron_left</span>
          </button>

          <button
            className="lightbox-nav lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedIndex(selectedIndex < allItems.length - 1 ? selectedIndex + 1 : 0);
            }}
          >
            <span className="material-icons">chevron_right</span>
          </button>

          <div className="lightbox-content-wrapper" onClick={(e) => e.stopPropagation()}>
            {hasFirebaseData && allItems[selectedIndex]?.title && (
              <h3 className="lightbox-title">{allItems[selectedIndex].title}</h3>
            )}
            <div className="lightbox-content">
              <div className="lightbox-image-container">
                <span className="lightbox-label">Before</span>
                <div className="lightbox-img-placeholder">
                  <img
                    src={getBeforeImageUrl(allItems[selectedIndex])}
                    alt={`Before - ${getItemTitle(allItems[selectedIndex])}`}
                    className="lightbox-img"
                    onLoad={(e) => e.target.setAttribute('data-loaded', 'true')}
                  />
                </div>
              </div>
              <div className="lightbox-divider" />
              <div className="lightbox-image-container">
                <span className="lightbox-label">After</span>
                <div className="lightbox-img-placeholder">
                  <img
                    src={getAfterImageUrl(allItems[selectedIndex])}
                    alt={`After - ${getItemTitle(allItems[selectedIndex])}`}
                    className="lightbox-img"
                    onLoad={(e) => e.target.setAttribute('data-loaded', 'true')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: ${SPACING_GROUP};
        }

        @media (min-width: 769px) and (max-width: 1199px) {
          .gallery-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .gallery-grid.limit-rows .gallery-item:nth-child(n+11) {
            display: none;
          }
        }

        @media (min-width: 1200px) {
          .gallery-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }

        .gallery-item {
          display: flex;
          gap: ${SPACING_TIGHT};
          aspect-ratio: 3 / 2;
          overflow: hidden;
          cursor: pointer;
          transition: transform 0.2s ease;
        }

        .gallery-item:hover {
          transform: scale(1.02);
        }

        .gallery-img-placeholder {
          flex: 1;
          height: 100%;
          background-color: #e5e5e5;
          border-radius: ${SPACING_TIGHT};
          overflow: hidden;
        }

        .gallery-half-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.3s ease-in-out;
        }

        .gallery-half-img[data-loaded="true"] {
          opacity: 1;
        }

        .gallery-divider {
          display: none;
        }

        .gallery-view-more {
          text-align: center;
          margin-top: ${SPACING_SECTION_LARGE};
        }

        .gallery-view-more-button {
          display: inline-block;
          padding: ${SPACING_BUTTON} ${SPACING_SECTION};
          font-size: 16px;
          font-weight: 600;
          color: var(--color-accent);
          border: 2px solid var(--color-accent);
          border-radius: 8px;
          text-decoration: none;
          transition: all 0.2s ease;
        }

        .gallery-view-more-button:hover {
          background: var(--color-accent);
          color: var(--color-accent-text, #fff);
        }

        /* Lightbox Styles */
        .lightbox-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: ${SPACING_CARD_BODY};
        }

        .lightbox-close {
          position: absolute;
          top: ${SPACING_CARD_BODY};
          right: ${SPACING_CARD_BODY};
          background: transparent;
          border: none;
          color: #fff;
          cursor: pointer;
          padding: ${SPACING_COMPACT};
          border-radius: 50%;
          transition: background 0.2s ease;
        }

        .lightbox-close:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .lightbox-close .material-icons {
          font-size: 32px;
        }

        .lightbox-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(0, 0, 0, 0.5);
          border: none;
          color: #fff;
          cursor: pointer;
          padding: ${SPACING_CARD} ${SPACING_COMPACT};
          border-radius: 8px;
          transition: background 0.2s ease;
          z-index: 10;
        }

        .lightbox-nav:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .lightbox-nav .material-icons {
          font-size: 48px;
        }

        .lightbox-prev {
          left: ${SPACING_CARD_BODY};
        }

        .lightbox-next {
          right: ${SPACING_CARD_BODY};
        }

        .lightbox-content-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: ${SPACING_CARD};
        }

        .lightbox-title {
          color: #fff;
          font-size: 24px;
          font-weight: 600;
          margin: 0;
          text-align: center;
        }

        .lightbox-content {
          display: flex;
          max-width: 85vw;
          max-height: 75vh;
          gap: 1px;
          align-items: center;
        }

        .lightbox-image-container {
          position: relative;
        }

        .lightbox-label {
          position: absolute;
          bottom: ${SPACING_CARD_BODY};
          left: ${SPACING_CARD_BODY};
          color: #fff;
          font-size: 36px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.01em;
          text-shadow: 0 2px 12px rgba(0, 0, 0, 0.9), 0 0 30px rgba(0, 0, 0, 0.6);
          z-index: 1;
        }

        .lightbox-img-placeholder {
          height: 65vh;
          aspect-ratio: 3 / 4;
          background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0.05) 0%,
            rgba(255, 255, 255, 0.15) 50%,
            rgba(255, 255, 255, 0.05) 100%
          );
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 8px;
          overflow: hidden;
        }

        @keyframes shimmer {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }

        .lightbox-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          opacity: 0;
          transition: opacity 0.3s ease-in-out;
        }

        .lightbox-img[data-loaded="true"] {
          opacity: 1;
        }

        .lightbox-divider {
          width: 4px;
          background: rgba(255, 255, 255, 0.3);
        }

        @media (max-width: 768px) {
          .gallery-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: ${SPACING_BUTTON};
          }

          .gallery-grid.limit-rows .gallery-item:nth-child(n+11) {
            display: none;
          }

          .lightbox-title {
            font-size: 18px;
          }

          .lightbox-content {
            flex-direction: column;
            gap: 1px;
          }

          .lightbox-img-placeholder {
            height: 35vh;
            aspect-ratio: 3 / 4;
          }

          .lightbox-divider {
            width: 100%;
            height: 4px;
          }

          .lightbox-label {
            font-size: 24px;
            bottom: 14px;
            left: 14px;
          }

          .lightbox-nav {
            padding: ${SPACING_BUTTON} ${SPACING_TIGHT};
          }

          .lightbox-nav .material-icons {
            font-size: 36px;
          }

          .lightbox-prev {
            left: 10px;
          }

          .lightbox-next {
            right: 10px;
          }
        }

        @media (max-width: 480px) {
          .gallery-grid {
            grid-template-columns: 1fr;
            gap: ${SPACING_CARD};
          }

          .gallery-grid.limit-rows .gallery-item:nth-child(n+6) {
            display: none;
          }
        }
      `}</style>
    </>
  );
}
